import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { AbstractComponent, Channel, ChannelService, WarningWord, WarningWordService } from 'lib-trend-core';
import { debounceTime } from 'rxjs';

@Component({
    selector: 'warning-word-edit-component',
    // templateUrl: './warning-word-edit.component.html',
    templateUrl: './warning-word-edit.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WarningWordEditComponent extends AbstractComponent {
  filterControlChannel: FormControl = new FormControl('');

  warningWord!: WarningWord;
  channel!: Channel;
  color: string = '#A5A6F6';
  listChannel!: Array<Channel>;
  allChannel!: Array<Channel>;

  keywords: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;


  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<WarningWordEditComponent>,
    public warningWordService: WarningWordService,
    @Inject(MAT_DIALOG_DATA) public data: WarningWord,
    private channelService: ChannelService,
  ) {
    super(injector);
    this.warningWord = {} as WarningWord;
    this.createForm();
    this.getListChannel();
    this.fetchFilterChannel();
  }

  ngOnInit() {
    this.isNew = (!!this.data ? false : true);

    if (!this.isNew) {
      this.warningWord = this.data;
      this.warningWord.channel = this.data.channel;

      this.keywords = Array.isArray(this.warningWord.keyword) ? [...this.warningWord.keyword] : [];
      this.color = this.warningWord.bgColor;

      this.formGroup.patchValue({
        channel: this.data.channel._id,
        bgColor: this.data.bgColor,
        title: this.data.title,
        keywords: this.keywords,
      }, { emitEvent: false });

      this.formGroup.get('keyword')?.reset('', { emitEvent: false });
    }
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      channel: [null, Validators.required],
      bgColor: [{ value: '#A5A6F6', disabled: true }, Validators.required],
      title: [null, Validators.required],
      keyword: [''],
      keywords: [[], Validators.required],
    });

    this.formGroup.valueChanges.subscribe(({ keyword, ...rest }) => {
      Object.assign(this.warningWord, rest);
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save() {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.formGroup.get('keywords')?.markAsTouched();
      this.validateAllFormFields(this.formGroup)
      return;
    }

    this.warningWord.bgColor = this.formGroup.get('bgColor').value;
    this.warningWord.keyword = this.keywords;

    if (this.isNew) {
      this.warningWordService.create(this.warningWord).subscribe({
        next: (value) => {
          this.dialogRef.close(value);
          this.alertService.success(this.t('warningWord.edit.savedSuccess'));
        },
        error: (err) => {
          this.alertService.error(err.error.message);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    } else {
      this.warningWordService.update(this.warningWord._id, this.warningWord).subscribe({
        next: (value) => {
          this.dialogRef.close(value);
          this.alertService.success(this.t('warningWord.edit.savedSuccess'));
        },
        error: (err) => {
          this.alertService.error(err.error.message);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    }
  }

  selectColor() {
    this.formGroup.get('bgColor').setValue(this.color);
  }

  add(event: Event): void {
    event.preventDefault();

    const inputCtrl = event.target as HTMLInputElement;
    const value = (inputCtrl?.value ?? '').toString().trim();
    if (!value) return;

    const exists = this.keywords.some(k => k === value);
    if (!exists) this.keywords = [...this.keywords, value];

    const arrCtrl = this.formGroup.get('keywords');
    arrCtrl?.setValue(this.keywords);
    arrCtrl?.markAsDirty();
    arrCtrl?.markAsTouched();
    arrCtrl?.updateValueAndValidity();

    inputCtrl.value = '';
  }

  remove(value: string): void {
    const idx = this.keywords.findIndex(k => k.toLowerCase() === value.toLowerCase());
    if (idx === -1) return;

    this.keywords = this.keywords.filter((_, i) => i !== idx);

    const arrCtrl = this.formGroup.get('keywords');
    arrCtrl?.setValue(this.keywords);
    arrCtrl?.markAsDirty();
    arrCtrl?.markAsTouched();
    arrCtrl?.updateValueAndValidity();
  }

  onSelectChannel(event: MatSelectChange): void {
    this.formGroup.get('channel').setValue(event.source.value);
    this.channel = event.source.value;
  }

  private getListChannel(): void {
    this.channelService.getList().subscribe({
      next: (value) => {
        this.allChannel = value;
        this.listChannel = value;
      },
      error: err => this.alertService.error(''),
    });
  }

  private fetchFilterChannel(): void {
    this.filterControlChannel.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('channel')?.value || null;

      this.listChannel = this.allChannel.filter(channel =>
        channel.name.toLowerCase().includes(value.toLowerCase()) &&
        channel._id !== currentValue
      );

      const existingChannel = this.allChannel.find(channel => channel._id === currentValue);
      if (existingChannel) {
        this.listChannel.push(existingChannel);
      }
    });
  }

  onFocusChannel(): void {
    this.listChannel = this.allChannel;
  }
}
